import React, { useEffect, useState } from 'react';
import { Route, Routes, useNavigate, useSearchParams } from 'react-router-dom';
import * as microsoftTeams from '@microsoft/teams-js';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import { UserRoleSelector } from 'store/user/selector';
import { ThunkTeamsAutoLogin } from 'store/auth/thunks';
import { ILoginResponse } from 'services/auth/types';

import {
  CHAT,
  CHATS,
  HOME,
  INVOICES,
  LOGIN,
  PASSWORD_RECOVERY,
  PROFILE,
  TICKET,
  TICKET_CREATE,
  TICKET_CREATE_FORM,
  TICKET_TEMPLATE,
  TICKETS,
} from './routes';

import Profile from 'pages/Profile';
import Login from 'pages/Login';
import Home from 'pages/Home';
import Tickets from 'pages/Tickets';
import Chats from 'pages/Chats';
import Invoices from 'pages/Invoices';
import Ticket from 'pages/Ticket';
import TicketTemplate from 'pages/TicketTemplate';
import RecoverPassword from 'pages/RecoverPassword';
import ProtectedRoute from './ProtectedRoute';
import Loader from 'components/Loader';
import Teams from 'pages/Teams';

import { Roles } from 'constants/roles';
import {
  LOCAL_STORAGE_REFRESH_TOKEN,
  LOCAL_STORAGE_TOKEN,
  LOCAL_STORAGE_TEAMS_CLIENT_TYPE
} from 'constants/storage';
import { setThemeMode } from '../store/themeMode';
import { UIModeSelector } from '../store/uiMode';
import { Mode } from '../constants/enums';
import ProtectedRouteOld from './ProtectedRouteOld';
import HomeOld from '../pages/HomeOld';
import TicketsOld from '../pages/TicketsOld';
import CreateTicketOld from '../pages/CreateTicketOld';
import CreateTicketForm from '../pages/CreateTicketForm';

export const Router: React.FC = () => {
  const [compactView, setCompactView] = useState(false);
  const [screen, setScreen] = useState<string | null>('');
  const [, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);

  const role = useAppSelector(UserRoleSelector);
  const isNewUI = useAppSelector(UIModeSelector);
  const urlParams = new URLSearchParams(window.location.search);
  const allRolesExceptUser: Roles[] = [Roles.AccountingUser, Roles.Admin, Roles.Manager];
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (urlParams.get('compactView')) {
      setCompactView(true);
    }
    if (urlParams.get('screen')) {
      setScreen(urlParams.get('screen'));
    }
  }, [window.location.search]);

  useEffect(() => {
    let params = {};
    if (compactView) {
      params = { compactView: 'true' };
    }
    if (screen) {
      params = { ...params, screen };
    }
    if (Object.keys(params).length) {
      setSearchParams(params);
    }
  }, [window.location.search, compactView, screen]);

  useEffect(() => {
    (async function () {
      await microsoftTeams.app
        .initialize()
        .then(() => {
          microsoftTeams.app.getContext().then(context => {
            if (context?.app?.theme) {
              const theme = context?.app?.theme === Mode.Dark ? Mode.Dark : Mode.Light;
              dispatch(setThemeMode(theme));
            }
            if (context?.user?.loginHint && context?.user?.id) {
              dispatch(
                ThunkTeamsAutoLogin({
                  userId: context.user.id,
                  userPrincipal: context.user.loginHint,
                }),
              )
                .unwrap()
                .then((res: ILoginResponse) => {
                  localStorage.setItem(LOCAL_STORAGE_TOKEN, res.access_token);
                  localStorage.setItem(LOCAL_STORAGE_REFRESH_TOKEN, res.refresh_token);
                  localStorage.setItem(LOCAL_STORAGE_TEAMS_CLIENT_TYPE, context.app.host.clientType);

                  const route = context.page?.subPageId;
                  navigate(route ?? `${TICKETS}`);
                })
                .finally(() => {
                  setLoading(false);
                });
            }
          });
        })
        .catch(e => {
          // eslint-disable-next-line no-console
          console.log('teams app', e);
          setLoading(false);
        });
    })();
  }, []);

  if (loading) return <Loader />;

  return (
    <Routes>
      {/* Unauthorized Pages */}
      <Route path={LOGIN} element={<Login />} />
      <Route path={PASSWORD_RECOVERY} element={<RecoverPassword />} />

      {!compactView ? (
        <Route
          path="/"
          element={
            isNewUI ? <ProtectedRoute noLayout={false} /> : <ProtectedRouteOld noLayout={false} />
          }
        >
          <Route path={HOME} element={isNewUI ? <Home /> : <HomeOld /> } />

          <Route path={TICKETS} element={isNewUI ? <Tickets /> : <TicketsOld />} />
          {!isNewUI && <Route path={TICKET_CREATE} element={<CreateTicketOld />} />}
          {!isNewUI && <Route path={TICKET_TEMPLATE} element={<TicketTemplate />} />}
          {!isNewUI && <Route path={TICKET_CREATE_FORM} element={<CreateTicketForm />} />}

          <Route path={CHATS} element={<Chats />}>
            <Route path={CHAT} element={<Chats />} />
          </Route>

          <Route path={TICKET} element={<Ticket />} />
          <Route path={PROFILE} element={<Profile />} />

          {/* Routes for all roles expect user */}
          {role && allRolesExceptUser.includes(role) && (
            <>
              <Route path={INVOICES} element={<Invoices />} />{' '}
            </>
          )}

          <Route path="/*" element={<Home />} />
        </Route>
      ) : (
        <Route path="/" element={<ProtectedRoute noLayout />}>
          <Route path={TICKETS} element={<Teams />}>
            <Route path={TICKET} element={<Tickets />} />
          </Route>
          <Route path={TICKET_TEMPLATE} element={<TicketTemplate useTeamsStyles />} />

          {/* Routes for all roles expect user */}
          {role && allRolesExceptUser.includes(role) && (
            <>
              <Route path={INVOICES} element={<Invoices />} />{' '}
            </>
          )}

          <Route path="/*" element={<Teams />} />
        </Route>
      )}
    </Routes>
  );
};
